import React, { useState, useEffect } from 'react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { 
  User, 
  Lock, 
  Bell, 
  Grid, 
  LogOut, 
  Settings, 
  Shield, 
  AlertCircle, 
  RefreshCw, 
  Download, 
  Trash2, 
  Key, 
  Smartphone, 
  UserCheck, 
  CheckCircle, 
  XCircle, 
  UploadCloud, 
  Calendar, 
  DollarSign, 
  Plane, 
  HelpCircle,
  Save,
  Eye,
  EyeOff,
  Edit3,
  Save as SaveIcon
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { SupportCenter } from '../Support/SupportCenter';
import { supabase } from '../../lib/supabase';
import { SidebarConfigModal, SidebarItem } from '../SidebarConfig/SidebarConfigModal';
import { safeLocalStorage } from '../../lib/utils';

// Simplified settings sections
const SETTINGS_SECTIONS = [
  { key: 'profile', label: 'Profile & Security', icon: <User className="w-5 h-5" /> },
  { key: 'preferences', label: 'Preferences', icon: <Settings className="w-5 h-5" /> },
  { key: 'modules', label: 'Modules', icon: <Grid className="w-5 h-5" /> },
  { key: 'support', label: 'Support & Help', icon: <HelpCircle className="w-5 h-5" /> },
];

function TopNavTabs({ activeSection, setActiveSection }) {
  return (
    <div className="flex border-b bg-white sticky top-0 z-10 rounded-t-lg">
      {SETTINGS_SECTIONS.map((section) => (
        <button
          key={section.key}
          className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 -mb-px ${
            activeSection === section.key 
              ? 'border-idecide-primary text-idecide-primary bg-idecide-primary/5' 
              : 'border-transparent text-gray-600 hover:bg-gray-50'
          }`}
          onClick={() => setActiveSection(section.key)}
        >
          {section.icon}
          {section.label}
        </button>
      ))}
    </div>
  );
}

function ProfileSettings({ onChange, state }) {
  const { user, profile, refreshProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    date_of_birth: profile?.date_of_birth || '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const { error } = await supabase
        .from('profiles')
        .update(formData)
        .eq('id', user.id);
      
      if (error) throw error;
      
      await refreshProfile();
      setIsEditing(false);
      setMessage('Profile updated successfully');
    } catch (error) {
      setMessage('Failed to update profile');
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: profile?.full_name || '',
      phone: profile?.phone || '',
      date_of_birth: profile?.date_of_birth || '',
    });
    setIsEditing(false);
    setMessage('');
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordMessage('Password must be at least 6 characters long');
      return;
    }

    setPasswordSaving(true);
    setPasswordMessage('');
    
    try {
      // First re-authenticate with current password
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordData.currentPassword,
      });
      
      if (authError) {
        setPasswordMessage('Current password is incorrect');
        setPasswordSaving(false);
        return;
      }
      
      // Update password
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });
      
      if (error) throw error;
      
      setPasswordMessage('Password updated successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      // Close modal after a short delay
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordMessage('');
      }, 2000);
      
    } catch (error) {
      setPasswordMessage('Failed to update password');
      console.error('Error updating password:', error);
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <User className="text-idecide-primary" size={24} />
              <h2 className="text-xl font-semibold">Profile Information</h2>
            </div>
            {!isEditing && (
              <Button 
                variant="outline" 
                onClick={() => setIsEditing(true)}
                icon={<Edit3 size={16} />}
              >
                Edit Profile
              </Button>
            )}
          </div>

          {message && (
            <div className={`mb-4 p-3 rounded-lg ${
              message.includes('successfully') 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-idecide-primary focus:border-transparent" 
                type="text" 
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50" 
                type="email" 
                value={user?.email || ''} 
                disabled 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-idecide-primary focus:border-transparent" 
                type="tel" 
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
              <input 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-idecide-primary focus:border-transparent" 
                type="date" 
                value={formData.date_of_birth}
                onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                disabled={!isEditing}
              />
            </div>
          </div>

          {isEditing && (
            <div className="flex gap-3 mt-6">
              <Button 
                variant="primary" 
                onClick={handleSave}
                disabled={saving}
                icon={saving ? <RefreshCw className="animate-spin" size={16} /> : <SaveIcon size={16} />}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="text-idecide-primary" size={24} />
            <h2 className="text-xl font-semibold">Security</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium">Two-Factor Authentication</h3>
                <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
              </div>
              <Button variant="outline">Enable 2FA</Button>
            </div>
            
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium">Change Password</h3>
                <p className="text-sm text-gray-600">Update your account password</p>
              </div>
              <Button variant="outline" onClick={() => setShowPasswordModal(true)}>Change Password</Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-6">Change Password</h2>
            
            {passwordMessage && (
              <div className={`mb-4 p-3 rounded-lg ${
                passwordMessage.includes('successfully') 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {passwordMessage}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                <input 
                  type="password" 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-idecide-primary focus:border-transparent" 
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input 
                  type="password" 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-idecide-primary focus:border-transparent" 
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                <input 
                  type="password" 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-idecide-primary focus:border-transparent" 
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button 
                variant="primary" 
                onClick={handlePasswordChange}
                disabled={passwordSaving}
                icon={passwordSaving ? <RefreshCw className="animate-spin" size={16} /> : <SaveIcon size={16} />}
              >
                {passwordSaving ? 'Updating...' : 'Update Password'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                  });
                  setPasswordMessage('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreferencesSettings({ onChange, state }) {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    weekly_digest: true,
    marketing_emails: false,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Load preferences from localStorage or database
    const saved = localStorage.getItem(`preferences_${user?.id}`);
    if (saved) {
      setPreferences(JSON.parse(saved));
    }
  }, [user]);

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      // Save to localStorage for now
      localStorage.setItem(`preferences_${user.id}`, JSON.stringify(preferences));
      
      // TODO: Save to database
      setMessage('Preferences saved successfully');
    } catch (error) {
      setMessage('Failed to save preferences');
      console.error('Error saving preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Bell className="text-idecide-primary" size={24} />
            <h2 className="text-xl font-semibold">Notification Preferences</h2>
          </div>

          {message && (
            <div className={`mb-4 p-3 rounded-lg ${
              message.includes('successfully') 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}

          <div className="space-y-4">
            {Object.entries(preferences).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <h3 className="font-medium capitalize">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {key === 'email_notifications' && 'Receive notifications via email'}
                    {key === 'sms_notifications' && 'Receive notifications via SMS'}
                    {key === 'push_notifications' && 'Receive push notifications in your browser'}
                    {key === 'weekly_digest' && 'Get a weekly summary of your activity'}
                    {key === 'marketing_emails' && 'Receive promotional emails and updates'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => handlePreferenceChange(key, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-idecide-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-idecide-primary"></div>
                </label>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-6">
            <Button 
              variant="primary" 
              onClick={handleSave}
              disabled={saving}
              icon={saving ? <RefreshCw className="animate-spin" size={16} /> : <SaveIcon size={16} />}
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function ModuleSettings({ onChange, state }) {
  const [showSidebarConfig, setShowSidebarConfig] = useState(false);
  const [sidebarItems, setSidebarItems] = useState<SidebarItem[]>([]);
  const { user } = useAuth();

  // Load current sidebar configuration
  useEffect(() => {
    const loadSidebarConfig = async () => {
      if (!user) return;
      
      try {
        // First try to load from localStorage for immediate feedback
        const localConfig = safeLocalStorage.getItem(`sidebar_config_${user.id}`);
        if (localConfig) {
          setSidebarItems(JSON.parse(localConfig));
        }
        
        // Then load from database
        const { data, error } = await supabase
          .from('user_preferences')
          .select('sidebar_items')
          .eq('user_id', user.id)
          .single();
          
        if (error && error.code !== 'PGRST116') {
          console.error('Failed to load sidebar configuration:', error);
          return;
        }
        
        if (data?.sidebar_items) {
          const dbItems = JSON.parse(data.sidebar_items);
          setSidebarItems(dbItems);
          // Update localStorage to match database
          safeLocalStorage.setItem(`sidebar_config_${user.id}`, JSON.stringify(dbItems));
        }
      } catch (err) {
        console.error('Error loading sidebar configuration:', err);
      }
    };
    
    loadSidebarConfig();
  }, [user]);

  const handleSaveSidebarConfig = async (items: SidebarItem[]) => {
    setSidebarItems(items);
    
    // Save to localStorage for immediate feedback
    if (user) {
      safeLocalStorage.setItem(`sidebar_config_${user.id}`, JSON.stringify(items));
      
      // Save to database
      try {
        const { error } = await supabase
          .from('user_preferences')
          .upsert({
            user_id: user.id,
            sidebar_items: JSON.stringify(items),
            updated_at: new Date().toISOString()
          }, { onConflict: 'user_id' });
          
        if (error) {
          console.error('Failed to save sidebar configuration:', error);
        } else {
          // Notify parent component of changes
          onChange({ sidebarItems: items });
        }
      } catch (err) {
        console.error('Error saving sidebar configuration:', err);
      }
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Grid className="text-idecide-primary" size={24} />
              <h2 className="text-xl font-semibold">Module Configuration</h2>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowSidebarConfig(true)}
            >
              Customize Sidebar
            </Button>
          </div>
          
          <p className="text-gray-600 mb-6">
            Configure which modules appear in your sidebar and customize their order. 
            You can show or hide modules based on your preferences.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Settings className="text-blue-600 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-blue-900">Sidebar Customization</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Click "Customize Sidebar" to drag and drop modules, show/hide them, and reorder your navigation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <SidebarConfigModal
        isOpen={showSidebarConfig}
        onClose={() => setShowSidebarConfig(false)}
        items={sidebarItems}
        onSave={handleSaveSidebarConfig}
      />
    </div>
  );
}

function AccountSettings() {
  const { user, signOut } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [password, setPassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');

  const handleExportData = async () => {
    setExporting(true);
    setExportMessage('');
    
    try {
      // Collect data from various tables
      const [profileData, calendarData, petsData, legalData, financialData, healthData, travelData, ndisData, trustedAdvisorsData, digitalEstateData, legacyData] = await Promise.allSettled([
        supabase.from('profiles').select('*').eq('id', user.id).single(),
        supabase.from('calendar_events').select('*').eq('user_id', user.id),
        supabase.from('pets').select('*').eq('user_id', user.id),
        supabase.from('legal_documents').select('*').eq('user_id', user.id),
        supabase.from('financial_accounts').select('*').eq('user_id', user.id),
        supabase.from('health_records').select('*').eq('user_id', user.id),
        supabase.from('travel_bookings').select('*').eq('user_id', user.id),
        supabase.from('ndis_goals').select('*').eq('user_id', user.id),
        supabase.from('trusted_advisors').select('*').eq('user_id', user.id),
        supabase.from('digital_estate_contacts').select('*').eq('user_id', user.id),
        supabase.from('legacy_journals').select('*').eq('user_id', user.id),
      ]);

      const exportData = {
        export_date: new Date().toISOString(),
        user_id: user.id,
        profile: profileData.status === 'fulfilled' ? profileData.value.data : null,
        calendar_events: calendarData.status === 'fulfilled' ? calendarData.value.data : [],
        pets: petsData.status === 'fulfilled' ? petsData.value.data : [],
        legal_documents: legalData.status === 'fulfilled' ? legalData.value.data : [],
        financial_accounts: financialData.status === 'fulfilled' ? financialData.value.data : [],
        health_records: healthData.status === 'fulfilled' ? healthData.value.data : [],
        travel_bookings: travelData.status === 'fulfilled' ? travelData.value.data : [],
        ndis_goals: ndisData.status === 'fulfilled' ? ndisData.value.data : [],
        trusted_advisors: trustedAdvisorsData.status === 'fulfilled' ? trustedAdvisorsData.value.data : [],
        digital_estate_contacts: digitalEstateData.status === 'fulfilled' ? digitalEstateData.value.data : [],
        legacy_journals: legacyData.status === 'fulfilled' ? legacyData.value.data : [],
      };

      // Create and download JSON file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `idecide-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportMessage('Data exported successfully');
    } catch (error) {
      setExportMessage('Failed to export data');
      console.error('Error exporting data:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setDeleteError('');
    try {
      // Step 2: Re-authenticate
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password,
      });
      if (authError) {
        setDeleteError('Incorrect password.');
        setLoading(false);
        return;
      }
      // Step 3: Delete account
      const { error } = await supabase.auth.deleteUser();
      if (error) {
        setDeleteError('Failed to delete account.');
        setLoading(false);
        return;
      }
      setDeleteSuccess(true);
    } catch (err) {
      setDeleteError('Unexpected error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <UserCheck className="text-idecide-primary" size={24} />
            <h2 className="text-xl font-semibold">Account Information</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Created</label>
              <input 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50" 
                type="text" 
                value={new Date(user?.created_at).toLocaleDateString() || 'Unknown'} 
                disabled 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Login</label>
              <input 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50" 
                type="text" 
                value={new Date().toLocaleString()} 
                disabled 
              />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Download className="text-idecide-primary" size={24} />
            <h2 className="text-xl font-semibold">Data Management</h2>
          </div>
          
          {exportMessage && (
            <div className={`mb-4 p-3 rounded-lg ${
              exportMessage.includes('successfully') 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {exportMessage}
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium">Export My Data</h3>
                <p className="text-sm text-gray-600">Download a copy of all your data</p>
              </div>
              <Button 
                variant="outline" 
                icon={exporting ? <RefreshCw className="animate-spin" size={16} /> : <Download size={16} />}
                onClick={handleExportData}
                disabled={exporting}
              >
                {exporting ? 'Exporting...' : 'Export Data'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Trash2 className="text-red-500" size={24} />
            <h2 className="text-xl font-semibold text-red-600">Danger Zone</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
              <div>
                <h3 className="font-medium text-red-900">Delete Account</h3>
                <p className="text-sm text-red-700">Permanently delete your account and all data</p>
              </div>
              <Button 
                variant="danger" 
                onClick={() => { setShowDeleteModal(true); setDeleteStep(1); }}
                icon={<Trash2 size={16} />}
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
            {deleteSuccess ? (
              <div className="text-center">
                <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
                <h2 className="text-xl font-bold mb-2">Account Deleted</h2>
                <p>Your account has been deleted. You will be logged out.</p>
              </div>
            ) : deleteStep === 1 ? (
              <>
                <h2 className="text-xl font-bold mb-4">Are you sure?</h2>
                <p className="mb-4">This will permanently delete your account and all data. This action cannot be undone.</p>
                <div className="flex gap-4 justify-end">
                  <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                  <Button variant="danger" onClick={() => setDeleteStep(2)}>Continue</Button>
                </div>
              </>
            ) : deleteStep === 2 ? (
              <>
                <h2 className="text-xl font-bold mb-4">Confirm Your Password</h2>
                <input 
                  type="password" 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 focus:ring-2 focus:ring-idecide-primary focus:border-transparent" 
                  placeholder="Enter your password" 
                  value={password} 
                  onChange={e => setPassword(e.target.value)} 
                />
                {deleteError && <div className="text-red-600 mb-2">{deleteError}</div>}
                <div className="flex gap-4 justify-end">
                  <Button variant="outline" onClick={() => setDeleteStep(1)}>Back</Button>
                  <Button variant="danger" loading={loading} onClick={() => setDeleteStep(3)}>Next</Button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold mb-4">Final Confirmation</h2>
                <p className="mb-4">This is your last chance. Are you absolutely sure you want to delete your account?</p>
                {deleteError && <div className="text-red-600 mb-2">{deleteError}</div>}
                <div className="flex gap-4 justify-end">
                  <Button variant="outline" onClick={() => setDeleteStep(2)}>Back</Button>
                  <Button variant="danger" loading={loading} onClick={handleDelete}>Delete Forever</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SettingsDashboard() {
  const [activeSection, setActiveSection] = useState('profile');
  const [settingsState, setSettingsState] = useState({});

  // Handler to update main settings state from subcomponents
  const handleSettingsChange = (section, changes) => {
    setSettingsState((prev) => ({ ...prev, [section]: { ...prev[section], ...changes } }));
  };

  return (
    <div className="max-w-4xl mx-auto min-h-[70vh]">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Manage your account preferences and configuration</p>
      </div>
      
      <Card className="overflow-hidden">
        <TopNavTabs activeSection={activeSection} setActiveSection={setActiveSection} />
        <div className="p-8">
          {activeSection === 'profile' && <ProfileSettings onChange={(changes) => handleSettingsChange('profile', changes)} state={settingsState.profile} />}
          {activeSection === 'preferences' && <PreferencesSettings onChange={(changes) => handleSettingsChange('preferences', changes)} state={settingsState.preferences} />}
          {activeSection === 'modules' && <ModuleSettings onChange={(changes) => handleSettingsChange('modules', changes)} state={settingsState.modules} />}
          {activeSection === 'support' && <SupportCenter />}
        </div>
      </Card>
    </div>
  );
}